import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';

// --- Types (mirror backend AdminInviteView in lambda/src/shared/services/signup) ---

export type AdminInviteStatus =
  | 'invited'
  | 'expired'
  | 'accepted'
  | 'cancelled';

// Subscription slugs — mirror backend enums (lambda/src/shared/constants.ts).
// Display labels live in the modal / table components.
export const REFRESH_FREQUENCIES = ['weekly', 'monthly'] as const;
export type RefreshFrequency = (typeof REFRESH_FREQUENCIES)[number];

export const SUBSCRIPTION_TARIFFS = [
  'digital_baseline',
  'digital_advanced',
  'cross_channel_growth',
  'full_scale_enterprise',
] as const;
export type SubscriptionTariff = (typeof SUBSCRIPTION_TARIFFS)[number];

export type SubscriptionSnapshot = {
  refreshFrequency: RefreshFrequency;
  tariff: SubscriptionTariff;
  offlineMediaAvailable: boolean;
  modelsAvailable: number;
  /** ISO calendar date `YYYY-MM-DD`, inclusive. */
  untilDue: string;
};

export type AdminInvite = {
  id: string;
  email: string;
  companyName: string;
  displayName: string | null;
  status: AdminInviteStatus;
  sentAt: string;
  /** Present on new staff-issued invites; may be missing on legacy rows. */
  subscription?: SubscriptionSnapshot;
};

type AdminInvitesResponse = {
  invites: AdminInvite[];
};

type AdminInviteResponse = {
  invite: AdminInvite;
};

type InviteCompanyAdminVariables = {
  email: string;
  companyName: string;
  displayName?: string;
  subscriptionRefreshFrequency: RefreshFrequency;
  subscriptionTariff: SubscriptionTariff;
  subscriptionOfflineMediaAvailable: boolean;
  subscriptionModelsAvailable: number;
  subscriptionUntilDue: string;
};

// --- Query Keys ---

const adminInvitesQueryKey = ['useAdminInvitesQuery'] as const;

// --- Hooks ---

export function useAdminInvitesQuery() {
  return useQuery(
    adminInvitesQueryKey,
    async () => {
      const response = await request.get<AdminInvitesResponse>(
        '/data/signup/admin-invites',
      );
      return response.data?.invites ?? [];
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
}

export function useInviteCompanyAdminMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (variables: InviteCompanyAdminVariables) => {
      const response = await request.post<AdminInviteResponse>(
        '/data/signup/send-invite-email',
        variables,
      );
      return response.data.invite;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(adminInvitesQueryKey);
      },
    },
  );
}

export function useResendAdminInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (inviteId: string) => {
      const response = await request.post<AdminInviteResponse>(
        '/data/signup/admin-invites/resend',
        { inviteId },
      );
      return response.data.invite;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(adminInvitesQueryKey);
      },
    },
  );
}

export function useCancelAdminInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (inviteId: string) => {
      const response = await request.delete<AdminInviteResponse>(
        '/data/signup/admin-invites',
        { params: { invite_id: inviteId } },
      );
      return response.data.invite;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(adminInvitesQueryKey);
      },
    },
  );
}
